import { useEffect, useState, type FormEvent } from "react";
import { CornerDownLeft, Search, SearchX, Sparkles, X } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { formatCurrency, formatDate } from "@/lib/format";
import { ExpenseRowsTable } from "@/components/ExpensesTable";
import { EmptyState, ErrorState, LoadingState } from "@/components/ExpenseUi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Kbd } from "@/components/ui/kbd";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";

// Each search is one model call, so it runs on Enter rather than per keystroke.

type ExpenseSearchResponse = components["schemas"]["ExpenseSearchResponse"];
type ExpenseSearchInterpretation =
  components["schemas"]["ExpenseSearchInterpretation"];

const EXAMPLES = [
  "client lunches",
  "taxis over £50 last month",
  "software without a receipt",
];

export function ExpenseSearchBox({
  query,
  onSearch,
}: {
  /** The search currently shown; empty when none is. */
  query: string;
  onSearch: (query: string) => void;
}) {
  const [draft, setDraft] = useState(query);

  // Follow the host when it changes the query (a cleared search, back navigation).
  useEffect(() => setDraft(query), [query]);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSearch(draft.trim());
  }

  // Typed but not yet searched: say how to run it, since nothing happens until Enter.
  const pending = draft.trim() !== "" && draft.trim() !== query;

  return (
    <form onSubmit={submit} className="relative mb-5" role="search">
      <Search className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={`Search spend: try "${EXAMPLES[0]}" or "${EXAMPLES[1]}"`}
        aria-label="Search expenses"
        className="h-12 rounded-lg pr-28 pl-11 text-base shadow-sm"
      />
      <div className="absolute inset-y-0 right-3 flex items-center gap-2">
        {pending ? (
          <Kbd className="h-6 gap-1 px-1.5">
            <CornerDownLeft />
            Enter
          </Kbd>
        ) : null}
        {draft ? (
          <button
            type="button"
            onClick={() => {
              setDraft("");
              onSearch("");
            }}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>
    </form>
  );
}

export function ExpenseSearchResults({
  query,
  onSelect,
  onClear,
}: {
  query: string;
  onSelect?: (expenseId: string) => void;
  onClear: () => void;
}) {
  const api = useDolfinClient();
  const [result, setResult] = useState<ExpenseSearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/v1/expenses/search", { params: { query: { Q: query } } })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !data) setError("Search failed");
        else setResult(data);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, query, attempt]);

  if (error)
    return (
      <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />
    );
  if (loading || !result) return <LoadingState label="Searching..." />;

  const expenses = result.data ?? [];
  const count =
    expenses.length === 0
      ? "No matches"
      : `${expenses.length} ${expenses.length === 1 ? "match" : "matches"}${result.interpretation?.text ? ", best first" : ""}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Searched for
          </span>
          <InterpretationChips interpretation={result.interpretation} />
          {result.semanticRanking === false ? (
            <span
              className="text-xs text-muted-foreground"
              title="Meaning-based search is off in this environment, so only names were matched"
            >
              names only
            </span>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-1 text-sm text-muted-foreground">
          <span className="tabular-nums">{count}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={onClear}
          >
            Clear
          </Button>
        </div>
      </div>

      {expenses.length === 0 ? (
        <EmptyState icon={SearchX} title="Nothing matches that search">
          Try describing what was bought, like "{EXAMPLES[2]}", or loosen the
          dates and amounts.
        </EmptyState>
      ) : (
        <ExpenseRowsTable expenses={expenses} onSelect={onSelect} />
      )}
    </div>
  );
}

function InterpretationChips({
  interpretation: q,
}: {
  interpretation?: ExpenseSearchInterpretation;
}) {
  if (!q) return null;
  const chips: { key: string; label: string; meaning?: boolean }[] = [];
  if (q.text) chips.push({ key: "text", label: q.text, meaning: true });
  if (q.from || q.to)
    chips.push({ key: "dates", label: dateLabel(q.from, q.to) });
  if (q.amountMin != null || q.amountMax != null)
    chips.push({ key: "amount", label: amountLabel(q) });
  if (q.currency && q.amountMin == null && q.amountMax == null)
    chips.push({ key: "currency", label: q.currency });
  if (q.needsReceipt === true)
    chips.push({ key: "receipt", label: "No receipt" });
  if (q.needsReceipt === false)
    chips.push({ key: "receipt", label: "Has a receipt" });

  return (
    <>
      {chips.map((chip) => (
        <Badge
          key={chip.key}
          variant="outline"
          className={cn(
            "h-6 gap-1 rounded-full px-2.5 text-xs font-medium",
            chip.meaning
              ? "border-primary/30 bg-primary/5 text-primary"
              : "text-foreground",
          )}
          title={
            chip.meaning ? "Matched by meaning" : "Applied as an exact filter"
          }
        >
          {chip.meaning ? <Sparkles className="size-3" /> : null}
          {chip.label}
        </Badge>
      ))}
    </>
  );
}

function dateLabel(from?: string | null, to?: string | null) {
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  if (from) return `From ${formatDate(from)}`;
  return `Until ${formatDate(to)}`;
}

function amountLabel(q: ExpenseSearchInterpretation) {
  const money = (v: number | string) =>
    q.currency ? formatCurrency(Number(v), q.currency) : String(v);
  if (q.amountMin != null && q.amountMax != null)
    return `${money(q.amountMin)} – ${money(q.amountMax)}`;
  if (q.amountMin != null) return `Over ${money(q.amountMin)}`;
  return `Under ${money(q.amountMax!)}`;
}
